import { useEffect, useState } from "react";
import { T } from "@/components/T";
import { useParams, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { fetchDeity } from "@/lib/api";
import { resolveImage, normalizeSrc } from "@/lib/images";
import KnowledgeCard from "@/components/KnowledgeCard";
import JourneyStrip from "@/components/JourneyStrip";
import PageEngage from "@/components/PageEngage";
import RichText from "@/components/RichText";
import Seo from "@/components/Seo";
import { MandalaMotif } from "@/components/MotifBackground";
import { ChevronRight, MapPin, Sparkles } from "lucide-react";

const GROUPS = [
  ["mantra", "मंत्र", "Mantras"],
  ["sukta", "सूक्त", "Suktas"],
  ["stotra", "स्तोत्र", "Stotras"],
  ["puja_vidhi", "पूजा विधि", "Pooja Vidhi"],
  ["katha", "कथा", "Kathas"],
];

const onImgError = (e) => { e.currentTarget.style.opacity = "0"; };
const stripHtml = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

export default function DeityDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const { pick } = useLang();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setD(null); setNotFound(false);
    fetchDeity(slug).then(setD).catch(() => setNotFound(true));
  }, [slug, location.pathname]);

  if (notFound) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3"><T>Deity not found.</T></div>;
  if (!d) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3">…</div>;

  const content = d.content || {};
  const img = resolveImage({ deity: d.name });
  const domains = String(d.domain || "").split(",").map((s) => s.trim()).filter(Boolean);
  const resourceCount = GROUPS.reduce((n, [t]) => n + (content[t]?.length || 0), 0);
  const templeCount = d.temples?.length || 0;

  // Anchor chips for present sections.
  const sections = GROUPS.filter(([t]) => content[t]?.length).map(([t, deva, en]) => ({ id: `sec-${t}`, deva, en }));
  if (templeCount) sections.push({ id: "sec-temples", deva: "मंदिर", en: "Temples" });
  const hasAny = resourceCount > 0 || templeCount > 0;

  return (
    <article className="relative pb-8" data-testid="deity-detail">
      <Seo path="/deities" title={`${d.name} — ${d.epithet || "Deity"}`} description={d.description} image={img} />

      {/* Immersive hero */}
      <section className="relative overflow-hidden border-b border-line bg-[var(--parchment)]">
        <MandalaMotif className="pointer-events-none absolute -left-24 top-1/2 hidden h-[420px] w-[420px] -translate-y-1/2 text-[var(--gold)] opacity-[0.06] md:block" />
        <div className="relative z-[2] mx-auto grid max-w-7xl gap-10 px-5 py-12 md:grid-cols-[1.05fr_0.95fr] md:items-center md:px-8 md:py-16">
          {/* Text */}
          <div>
            <nav className="flex items-center gap-1.5 text-xs text-ink-3">
              <Link to="/" className="hover:text-saffron"><T>Dharmartha</T></Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/deities" className="hover:text-saffron">{pick("देवी-देवता", "Deities")}</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-ink-2">{d.name}</span>
            </nav>
            {(d.epithet || d.epithet_hi) && <p className="mt-6 overline-label text-gold">{pick("देवता", "Deity")}</p>}
            <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="mt-2 font-deva text-5xl leading-tight text-ink deva-lh md:text-7xl" data-testid="deity-title">{d.deva_name}</motion.h1>
            <p className="mt-2 font-serif-latin text-2xl italic text-ink-2">{d.name}</p>
            {(d.epithet || d.epithet_hi) && <p className="mt-5 max-w-[52ch] font-serif-latin text-lg italic leading-relaxed text-ink-2">{stripHtml(pick(d.epithet_hi, d.epithet))}</p>}

            {domains.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {domains.map((x) => (
                  <span key={x} className="border border-[var(--gold-muted)] bg-surface/60 px-3 py-1 text-xs text-ink-2">{x}</span>
                ))}
              </div>
            )}
          </div>

          {/* Framed artwork */}
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
            className="relative mx-auto w-full max-w-sm md:max-w-none">
            <div className="relative overflow-hidden border border-[var(--gold-muted)] bg-[var(--surface-alt)] shadow-xl" style={{ aspectRatio: "4/5" }}>
              <img src={img} alt={d.name} onError={onImgError} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
              <p className="absolute bottom-4 left-5 font-deva text-4xl text-white deva-lh drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]">{d.deva_name}</p>
            </div>
            <span className="absolute -right-3 -top-3 hidden h-16 w-16 border border-[var(--gold-muted)] md:block" aria-hidden="true" />
          </motion.div>
        </div>

        {/* Anchor chips */}
        {sections.length > 1 && (
          <div className="relative z-[2] mx-auto max-w-7xl px-5 pb-8 md:px-8">
            <div className="flex flex-wrap gap-2">
              {sections.map((s) => (
                <a key={s.id} href={`#${s.id}`} className="inline-flex items-center gap-2 border border-line bg-surface/70 px-3 py-1.5 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
                  <span className="font-deva text-saffron/70">{s.deva[0]}</span> {pick(s.deva, s.en)}
                </a>
              ))}
            </div>
          </div>
        )}
      </section>

      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {/* About / overview — admin rich text rendered properly (not raw HTML) */}
        {(d.description || d.description_hi) && (
          <section className="mt-12 scroll-mt-24" data-testid="deity-about">
            <div className="mb-6 flex items-baseline gap-3">
              <span className="h-px w-8 bg-[var(--gold)]" />
              <h2 className="font-deva text-2xl text-saffron deva-lh">परिचय</h2>
              <span className="overline-label text-ink-3">{pick("परिचय", "About")}</span>
            </div>
            <RichText value={pick(d.description_hi, d.description)} className="max-w-[72ch] text-[15px] leading-[1.9] text-ink-2 md:text-base" />
          </section>
        )}

        {/* Significance — highlighted callout */}
        {(d.significance || d.significance_hi) && (
          <section className="mt-12" data-testid="deity-significance">
            <div className="relative overflow-hidden border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.06)] p-6 md:p-8">
              <span className="absolute left-0 top-0 h-full w-1 bg-[var(--gold)]" aria-hidden="true" />
              <p className="mb-2.5 flex items-center gap-2 overline-label text-gold"><Sparkles className="h-4 w-4" /> {pick("महत्त्व", "Significance")}</p>
              <RichText value={pick(d.significance_hi, d.significance)} className="max-w-[72ch] font-serif-latin text-lg leading-relaxed text-ink" />
            </div>
          </section>
        )}

        {!hasAny && <p className="mt-16 text-center text-ink-3">{pick("संबंधित सामग्री शीघ्र जोड़ी जा रही है।", "Associated content is being added.")}</p>}

        {GROUPS.map(([type, deva, en]) =>
          content[type]?.length ? (
            <section key={type} id={`sec-${type}`} className="mt-14 scroll-mt-24" data-testid={`deity-group-${type}`}>
              <div className="mb-6 flex items-baseline gap-3">
                <span className="h-px w-8 bg-[var(--gold)]" />
                <h2 className="font-deva text-2xl text-saffron deva-lh">{deva}</h2>
                <span className="overline-label text-ink-3">{en}</span>
              </div>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {content[type].map((c) => <KnowledgeCard key={c.slug} item={c} showImage />)}
              </div>
            </section>
          ) : null
        )}

        {templeCount > 0 && (
          <section id="sec-temples" className="mt-14 scroll-mt-24" data-testid="deity-group-temples">
            <div className="mb-6 flex items-baseline gap-3">
              <span className="h-px w-8 bg-[var(--gold)]" />
              <h2 className="font-deva text-2xl text-saffron deva-lh">मंदिर</h2>
              <span className="overline-label text-ink-3"><T>Temples</T></span>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {d.temples.map((tp) => (
                <Link key={tp.slug} to={`/temples/${tp.slug}`} className="group flex flex-col overflow-hidden border border-line bg-[var(--surface)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-lg">
                  <div className="relative h-44 overflow-hidden bg-[var(--surface-alt)]">
                    <img src={normalizeSrc(tp.image_url || tp.image) || img} alt={tp.name} loading="lazy" onError={onImgError} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    {tp.deva_name && <p className="absolute bottom-3 left-4 font-deva text-xl text-white/95 deva-lh">{tp.deva_name}</p>}
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-serif-latin text-lg text-ink">{tp.name}</h3>
                    <p className="mt-1.5 flex items-center gap-1.5 text-sm text-ink-3"><MapPin className="h-3.5 w-3.5" /> {[tp.location, tp.state].filter(Boolean).join(", ")}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

      </div>

      <PageEngage refType="deity" refSlug={slug} refTitle={d.name || d.deva_name} />

      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <JourneyStrip links={["mantras", "stotras", "rituals", "festivals", "temples"]} />
      </div>
    </article>
  );
}
